import {
  Alert,
  Box,
  Button,
  Checkbox,
  Divider,
  FormControlLabel,
  FormGroup,
  Typography,
} from '@mui/material';
import { useCallback, useContext, useEffect, useState } from 'react';
import CoursesApi from '../../../../../apis/CoursesApi';
import UtilitiesApi from '../../../../../apis/UtilitiesApi';
import LoadingCircle from '../../../../../components/LoadingCircle/LoadingCircle';
import UnexpectedError from '../../../../../components/UnexpectedError/UnexpectedError';
import SnackbarContext from '../../../../../contexts/SnackbarContext';
import { UtilitiesCourseProgressDto } from '../../../../../generated-types/utilitiesCourseProgressDto';
import { UtilitiesUserSearchDto } from '../../../../../generated-types/utilitiesUserSearchDto';
import useApi from '../../../../../lib/useApi';
import UserSearch from '../components/UserSearch';

/**
 * A utility that allows the admin to adjust the completed modules for a user
 */
const CompleteModuleUtility = () => {
  const [selectedUser, setSelectedUser] =
    useState<UtilitiesUserSearchDto | null>(null);
  const [courseProgress, setCourseProgress] =
    useState<UtilitiesCourseProgressDto>();
  const [initializing, setInitializing] = useState(true);
  const [courseMapping, setCourseMapping] = useState<Record<string, string>>();
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(false);
  const snackbar = useContext(SnackbarContext);
  const utilitiesApi = useApi(UtilitiesApi);
  const coursesApi = useApi(CoursesApi);

  /**
   * Initialize the course mapping
   */
  useEffect(() => {
    setInitializing(true);
    coursesApi
      .GetIdNameMapping()
      .then(setCourseMapping)
      .catch(() => {
        setError(true);
        snackbar.setAlert(
          <Alert severity="error" variant="filled">
            Error occurred while loading the course mappings. Please refresh to
            try again. If this continues, contact us.
          </Alert>
        );
        snackbar.triggerSnackbar();
      })
      .finally(() => setInitializing(false));
  }, [coursesApi, snackbar]);

  /**
   * Load the course progress for the selected user
   */
  useEffect(() => {
    if (selectedUser) {
      setIsLoading(true);
      setError(false);
      utilitiesApi
        .GetUserCourseProgress(selectedUser.id)
        .then(setCourseProgress)
        .catch(() => setError(true))
        .finally(() => {
          setIsLoading(false);
        });
    }
  }, [selectedUser, utilitiesApi]);

  /**
   * Save the course progress for the selected user
   */
  const saveProgress = useCallback(() => {
    // This should happen, but handling an edge case
    if (selectedUser == null || courseProgress == undefined) return;

    // Save the user
    setIsSaving(true);
    utilitiesApi
      .SaveCourseProgress(selectedUser.id, courseProgress)
      .then(() => {
        snackbar.setAlert(
          <Alert severity="success" variant="filled">
            Successfully set completed modules.
          </Alert>
        );
      })
      .catch(() => {
        snackbar.setAlert(
          <Alert severity="error" variant="filled">
            An error occurred while setting the user&apos;s level. Please try
            again. If this continues, contact Alterac.
          </Alert>
        );
      })
      .finally(() => {
        snackbar.triggerSnackbar();
        setIsSaving(false);
      });
  }, [courseProgress, selectedUser, snackbar, utilitiesApi]);

  /**
   * Cancel the selection
   */
  const cancel = useCallback(() => {
    setSelectedUser(null);
    setCourseProgress(undefined);
  }, []);

  /**
   * Handle when the module checkbox was changed
   */
  const handleCheckboxChange = useCallback(
    (courseId: string, moduleId: string) =>
      (event: React.ChangeEvent<HTMLInputElement>) => {
        // Get the current modules for the given course ID
        const modules = courseProgress!.progress[courseId];

        // Update the specific module's completion status
        const updatedModules = modules.map((module) =>
          module.moduleId === moduleId
            ? { ...module, complete: event.target.checked }
            : module
        );

        // Create an updated progress object for all courses
        const updatedProgress = {
          ...courseProgress!.progress,
          [courseId]: updatedModules,
        };

        // Update the course progress
        setCourseProgress({ progress: updatedProgress });
      },
    [courseProgress]
  );

  return (
    <Box position="relative" height="100%">
      {error ? (
        <UnexpectedError errorContext="within the complete module utility" />
      ) : (
        <Box
          sx={{
            pointerEvents: isSaving ? 'none' : 'initial',
            filter: isSaving ? 'blur(5px)' : 'initial',
          }}
        >
          <Box marginBottom={2}>
            <UserSearch
              onSelectedUser={setSelectedUser}
              selectedUser={selectedUser}
            />
          </Box>
          {selectedUser && courseProgress && courseMapping && (
            <Box>
              {Object.entries(courseMapping).map(([courseId, courseTitle]) => (
                <Box key={courseId}>
                  <Typography variant="h6" gutterBottom>
                    {courseTitle}
                  </Typography>
                  <FormGroup>
                    {courseProgress.progress[courseId].map((module) => (
                      <FormControlLabel
                        key={module.moduleId}
                        control={
                          <Checkbox
                            checked={module.complete}
                            onChange={handleCheckboxChange(
                              courseId,
                              module.moduleId
                            )}
                          />
                        }
                        label={module.moduleTitle}
                      />
                    ))}
                  </FormGroup>
                  <Divider sx={{ my: 4 }} />
                </Box>
              ))}
              <Box pb={4}>
                <Button variant="outlined" color="secondary" onClick={cancel}>
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  color="success"
                  sx={{ marginLeft: '1em' }}
                  onClick={saveProgress}
                >
                  Save
                </Button>
              </Box>
            </Box>
          )}
        </Box>
      )}
      <LoadingCircle isLoading={isSaving} loadingText="Saving..." />
      <LoadingCircle isLoading={isLoading} loadingText="Loading user..." />
      <LoadingCircle isLoading={initializing} loadingText="Initializing..." />
    </Box>
  );
};

export default CompleteModuleUtility;
